import { getCache, setCache } from "./cache";
import { apiBaseUrl } from "./config";
import { sendDelete, sendGetRequest, sendPost, sendPut, setUtilsToken } from "./utils";

const baseUrl = `${apiBaseUrl}/org`;
let token;

function setOrgToken(t) {
  token = t;
  setUtilsToken(t);
}

async function getOrg(includeDisabled = false) {
  const url = includeDisabled ? `${baseUrl}/admin` : `${baseUrl}/`;
  if (includeDisabled) {
    return await sendGetRequest(url);
  }

  const r = await fetch(url);
  const data = await r.json();
  return data;
}

async function getEtapas(type) {
  const cacheKey = `etapas${type ? `_${type}` : ''}`;
  const cached = getCache(cacheKey);
  if (cached) {
    return cached;
  }
  let url = `${baseUrl}/etapas`;
  if (type) {
    url += `?type=${type}`;
  }
  const r = await fetch(url);
  const data = await r.json();
  setCache(cacheKey, structuredClone(data));
  return data;
}

async function getCoordinadoresDeEquipoRoles() {
  const url = `${baseUrl}/roles/coordinadoresDeEquipo`;
  const r = await fetch(url);
  const data = await r.json();
  return data;
}

async function putRegion(label, token) {
  const url = `${baseUrl}/region`;
  const body= { label: label };
  const r = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await r.json();
  if (!r.ok) {
    throw data;
  }
  return data;
}

async function putDiocesis(label, region, usccbDiocesisId, token) {
  const url = `${baseUrl}/diocesis`;
  const body= { label: label, region: region, usccbDiocesisId: usccbDiocesisId };
  const r = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await r.json();
  if (!r.ok) {
    throw data;
  }
  return data;
}

async function putComunidad(label, diocesis, address) {
  const url = `${baseUrl}/comunidad`;
  const body= { label: label, diocesis: diocesis, address: address };
  const r = sendPut(url, body);
  return r;
}

async function getUsccbOrg() {
  const url = `${baseUrl}/usccb`;
  const r = await fetch(url);
  const data = await r.json();
  return data;
}

async function getComunidad(comunidadId, ciclo) {
  let url = `${baseUrl}/comunidad/${comunidadId}`;
  if (ciclo) {
    url += `?ciclo=${ciclo}`;
  }
  return sendGetRequest(url);
}

async function getComunidadAdmin(comunidadId, ciclo) {
  let url = `${baseUrl}/comunidad/${comunidadId}/admin`;
  if (ciclo) {
    url += `?ciclo=${ciclo}`;
  }
  return sendGetRequest(url);
}

async function saveComunidadAdmin(comunidadId, payload) {
  const url = `${baseUrl}/comunidad/${comunidadId}/admin`;
  const r = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await r.json();
  if (!r.ok) {
    throw data;
  }
  return data;
}

async function updateComunidadRegistrationStatus(comunidadId, enabledForRegistration) {
  const url = `${baseUrl}/comunidad/${comunidadId}/registration-status`;
  return await sendPut(url, { enabledForRegistration });
}

async function putEquipo(ciclo, comunidadId, etapaId, miembros) {
  const url = `${baseUrl}/comunidad/${comunidadId}/equipo`;
  const body= { ciclo, comunidadId, etapaId, miembros };
  const r = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await r.json();
  if (!r.ok) {
    throw data;
  }
  return data;
}
async function getEquipo(equipoId) {
  let url = `${baseUrl}/equipo/${equipoId}`;
  return sendGetRequest(url);
}
async function getEquipos(comunidadId, ciclo, etapaId) {
  let url = `${baseUrl}/comunidad/${comunidadId}/equipo`;
  let query = [];
  if (ciclo) {
    query.push(`ciclo=${ciclo}`);
  }
  if (etapaId) {
    query.push(`etapaId=${etapaId}`);
  }
  if (query.length) {
    url += '?' + query.join('&');
  }
  const r = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await r.json();
  return data;
}
async function addTeamMembers(teamId, members) {
  const url = `${baseUrl}/equipo/${teamId}/members`;
  const body= { members };
  await sendPut(url, body);
}

/**
 * Move member to a new team.
 *
 * @param {integer} teamId
 * @param {integer} membershipId
 * @param {integer} newTeamId
 * @return {Promise<void>}
 */
async function updateTeamMember(teamId, membershipId, newTeamId) {
  const url = `${baseUrl}/equipo/${teamId}/members/${membershipId}`;
  const body= { newTeamId: newTeamId };
  await sendPost(token, url, body);
}

/**
 * Remove member from a team.
 *
 * @param {integer} teamId
 * @param {integer} membershipId
 * @return {Promise<void>}
 */
async function removeTeamMember(teamId, membershipId) {
  const url = `${baseUrl}/equipo/${teamId}/members/${membershipId}`;
  await sendDelete(url);
}

async function getDiocesis(diocesisId, ciclo) {
  let url = `${baseUrl}/diocesis/${diocesisId}`;
  if (ciclo) {
    url += `?ciclo=${ciclo}`;
  }
  return await sendGetRequest(url);
}

async function getRegion(regionId, ciclo) {
  let url = `${baseUrl}/regiones/${regionId}`;
  if (ciclo) {
    url += `?ciclo=${ciclo}`;
  }
  return await sendGetRequest(url);
}

async function getTemasByEtapa(etapaId) {
  const url = `${baseUrl}/etapas/${etapaId}/temas`;
  return await sendGetRequest(url);
}

async function putReporteTema(equipoId, reporte) {
  const url = `${baseUrl}/equipo/${equipoId}/temas`;
  const body= reporte;
  return await sendPut(url, body);
}

export {
  setOrgToken,
  getOrg,
  getEtapas,
  getCoordinadoresDeEquipoRoles,
  putRegion,
  putDiocesis,
  putComunidad,
  getUsccbOrg,
  getComunidad,
  getComunidadAdmin,
  saveComunidadAdmin,
  updateComunidadRegistrationStatus,
  putEquipo,
  getEquipo,
  getEquipos,
  addTeamMembers,
  updateTeamMember,
  removeTeamMember,
  getDiocesis,
  getRegion,
  getTemasByEtapa,
  putReporteTema
};
